// IMPORTS
import { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

// COMPONENTS
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import InformationBox from "../../components/InformationBox";
import PortalDropdown from "../../components/PortalDropdown";

// UTILS & SERVICES
import { UserAuth } from "../../utils/AuthContext";
import { DataContext } from "../../utils/DataContext";
import { getBankSuppliersForPage } from "../../services/bank/bankSupplierServices";

// ASSETS
import PageWrapper from "../../components/PageWrapper";
import BackToDashboard from "../../components/BackToDashboard";
import useLanguage from "../../utils/useLanguage";

export default function BankSuppliersPage() {
  // HOOKS
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const { loading, setLoading } = useContext(DataContext);
  const { currentDirection } = useLanguage();

  // STATES - Supplier Data
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);

  // STATES - Search & Filter
  // const [searchQuery, setSearchQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [requestVersion, setRequestVersion] = useState(0);

  const [selectedFilter, setSelectedFilter] = useState({
    title: "supplierName",
    value: "supplierName",
  });
  const [sortOrder, setSortOrder] = useState({
    title: "ascending",
    value: "asc",
  });

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // STATES - UI Control
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 10,
  });
  const [errorMessage, setErrorMessage] = useState("");

  // API HANDLERS
  const getSuppliers = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const searchParams = {
        page: pagination.currentPage,
        limit: 5,
      };

      if (dateFrom) {
        searchParams.dateFrom = dateFrom;
      }
      if (dateTo) {
        searchParams.dateTo = dateTo;
      }
      if (selectedFilter.value === "supplierName" && appliedSearchQuery) {
        searchParams.supplierName = appliedSearchQuery;
      }
      if (selectedFilter.value === "country" && appliedSearchQuery) {
        searchParams.country = appliedSearchQuery;
      }
      if (selectedFilter.value === "city" && appliedSearchQuery) {
        searchParams.city = appliedSearchQuery;
      }

      // Add sorting for invoice-related fields
      if (selectedFilter.value === "totalInvoices") {
        searchParams.invoiceSort = sortOrder.value;
      } else if (selectedFilter.value === "acceptedInvoices") {
        // Changed
        searchParams.acceptedSort = sortOrder.value; // Changed
      } else if (selectedFilter.value === "unacceptedInvoices") {
        // Changed
        searchParams.unacceptedSort = sortOrder.value; // Changed
      } else if (selectedFilter.value === "payments") {
        searchParams.paymentSort = sortOrder.value;
      }

      const response = await getBankSuppliersForPage(searchParams);

      if (response.status === 200) {
        setSuppliers(response.data.suppliers || []);
        setPagination({
          currentPage: response.data.pagination.currentPage,
          totalPages: response.data.pagination.totalPages,
          totalRecords: response.data.pagination.totalItems,
          itemsPerPage: response.data.pagination.itemsPerPage,
        });
      }
    } catch (error) {
      console.error("Failed to get suppliers:", error);
      setErrorMessage(
        error.response?.data?.error || "Failed to fetch suppliers",
      );
    } finally {
      setLoading(false);
    }
  };

  // EVENT HANDLERS

  const handleFilterChange = (selected) => {
    setSelectedFilter(selected);
    setSearchQuery("");
    setAppliedSearchQuery("");
    setSortOrder({ title: "ascending", value: "asc" });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSearch = () => {
    setAppliedSearchQuery(searchQuery);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setRequestVersion((v) => v + 1);
  };
  const isSortableField = !["supplierName", "country", "city"].includes(
    selectedFilter.value,
  );

  // FILTER OPTIONS
  const filterOptions = [
    { title: t("supplierName"), value: "supplierName" },
    { title: t("country"), value: "country" },
    { title: t("city"), value: "city" },
    { title: t("numberOfInvoices"), value: "totalInvoices" },
    { title: t("acceptedInvoices"), value: "acceptedInvoices" }, // Changed
    { title: t("unacceptedInvoices"), value: "unacceptedInvoices" }, // Changed
    { title: t("numberOfPayments"), value: "payments" },
  ];

  // SORT OPTIONS
  const sortOptions = [
    { title: t("ascending"), value: "asc" },
    { title: t("descending"), value: "desc" },
  ];

  // TABLE CONFIGURATION
  const titles = [
    { value: "receiverName" },
    { value: "company" },
    { value: "country" },
    { value: "city" },
    { value: "numberOfInvoices" },
    { value: "acceptedInvoices" }, // Changed
    { value: "unacceptedInvoices" }, // Changed
    { value: "numberOfPayments" },
  ];

  const rows = suppliers?.map((item) => [
    {
      type: TABLE_CELL_TYPES.LINK,
      value: item?.name || "-",
      to: `/bank-suppliers/${item.id}`,
    },
    {
      value: item?.company || "-",
      type: TABLE_CELL_TYPES.TEXT,
    },
    {
      value: item?.country || "-",
      type: TABLE_CELL_TYPES.TEXT,
    },
    {
      value: item?.city || "-",
      type: TABLE_CELL_TYPES.TEXT,
    },
    {
      value: item?.invoiceStats?.total || 0,
      type: TABLE_CELL_TYPES.TEXT,
    },
    {
      value: item?.invoiceStats?.accepted || 0, // Changed
      type: TABLE_CELL_TYPES.TEXT,
    },
    {
      value: item?.invoiceStats?.unaccepted || 0, // Changed
      type: TABLE_CELL_TYPES.TEXT,
    },
    {
      value: item?.invoiceStats?.payments || 0,
      type: TABLE_CELL_TYPES.TEXT,
    },
  ]);

  const handleEnter = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();

    if (
      selectedFilter.value === "supplierName" ||
      selectedFilter.value === "country" ||
      selectedFilter.value === "city"
    ) {
      handleSearch();
      return;
    }
  };
  const handleSort = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setRequestVersion((v) => v + 1);
  };

  // USE EFFECTS
  useEffect(() => {
    if (userData?.organizationName) {
      getSuppliers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.currentPage,
    selectedFilter.value,
    sortOrder.value,
    dateFrom,
    dateTo,

    appliedSearchQuery,
    requestVersion,

    userData?.token,
    userData?.organizationId,
    userData?.organizationName,
  ]);

  // RENDER
  return (
    <PageWrapper >
      <div>

        <h3>{t("receivers")}</h3>

        {errorMessage && <ErrorBox>{errorMessage}</ErrorBox>}

        {/* DATE RANGE FILTER */}
        <div className="o-column-row-switcher o-column--gap-size_300 u-margin--bottom_500 u-margin--top_500">
          <CustomInput
            name="dateFrom"
            title={t("from")}
            type="date"
            thick
            onChange={(e) => {
              setDateFrom(e?.target?.value);
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
            resetSearch={() => {
              setDateFrom("");
            }}
            value={dateFrom}
            onKeyDown={handleEnter}
          />
          <CustomInput
            name="dateTo"
            title={t("to")}
            type="date"
            thick
            onChange={(e) => {
              setDateTo(e?.target?.value);
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
            resetSearch={() => {
              setDateTo("");
            }}
            value={dateTo}
            onKeyDown={handleEnter}
          />
        </div>

        {/* SEARCH/SORT FUNCTIONALITY */}
        <div className="c-search-bar u-margin--bottom_500">
          <section className="c-search-bar-label-input">
            <label>
              {t("searchTitle")} {t(selectedFilter?.title)}:
            </label>
            {isSortableField ? (
              <PortalDropdown
                options={sortOptions}
                selected={sortOrder}
                setSelected={setSortOrder}
                thick
              />
            ) : (
              <div className="u-width--min-width_230">
                <CustomInput
                  placeholder={`${t("searchTitle")} ${t(
                    selectedFilter?.value,
                  )}`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  resetSearch={() => {
                    setSearchQuery("");
                    setAppliedSearchQuery("");
                    setPagination((prev) => ({ ...prev, currentPage: 1 }));
                    setRequestVersion((v) => v + 1);
                  }}
                  onKeyDown={handleEnter}
                  thick
                />
              </div>
            )}
          </section>
          <div className="o-row o-row--gap-size_400">
            <PortalDropdown
              options={filterOptions}
              selected={selectedFilter}
              setSelected={handleFilterChange}
              thick
            />
            <CustomButton
              title={t("search")}
              onClick={isSortableField ? handleSort : handleSearch}
              icon="search"
              iconSize={20}
            />
          </div>
        </div>

        {/* SUPPLIERS TABLE */}
        {loading ? (
          <Loader />
        ) : suppliers.length === 0 ? (
          <InformationBox>
            <p>{t("noSuppliers")}</p>
          </InformationBox>
        ) : (
          <ul className="users-list">
            <RoundedTable
              titles={titles}
              rows={rows}
              fullData={suppliers}
              pagination={pagination}
              setPagination={setPagination}
              selectedRows={selectedSuppliers}
              setSelectedRows={setSelectedSuppliers}
            />
          </ul>
        )}
      </div>
    </PageWrapper>
  );
}
